import type { AgenticUxTransaction } from "@/store/project-slice";
import type {
  ChannelDispatchStatus,
} from "@/store/ide-v2-slice";
import {
  openPencilImportActivityTitle,
  type OpenPencilImportActivity,
} from "@/features/openpencil-figma/openpencil-import-activity";
import type { RailItemId } from "../types";
import {
  resolveStudioAuthorityDomain,
  type StudioAuthorityDomain,
} from "../studio-authority-workspaces";
import type {
  StudioCockpitActivity,
  StudioCockpitActivityKind,
  StudioCockpitTab,
} from "./studio-cockpit-activity";

const APP_OVERVIEW = {
  domain: "primary",
  authorityView: "overview",
  railItem: "preview",
} as const;

const STATUS_QUALITY = {
  domain: "insights",
  authorityView: "quality",
  railItem: "evidence",
} as const;

/**
 * A .fig import lives in the Design domain's Sources view, but a BLOCKED one
 * is a blocker — and per the Studio UX canon (§3.1 Status = "what is done,
 * what is blocked, on what evidence"; §5 `>>>` and the badge express real
 * blockers) blockers must be visible from the Status tab, not only from a
 * Design sub-panel two clicks away. The activity is therefore projected onto
 * the `status` tab while keeping the exact source destination, so activating
 * it lands the creator on the surface that holds the evidence.
 */
const DESIGN_SOURCES = {
  domain: "design",
  authorityView: "sources",
  railItem: "source",
} as const;

export interface StudioCockpitSelectorState {
  activeRailItem: RailItemId;
  channelDispatchStatus: ChannelDispatchStatus;
  agenticUxTransaction: AgenticUxTransaction | null;
  validatorRunning: boolean;
  validatorError: string | null;
  validatorUnresolvedError: { message: string; failedAt: number; runId: number } | null;
  validatorLastRunAt?: number | null;
  validationReport: unknown;
  reviewedCockpitActivityIds: string[];
  /** Optional so every existing caller/test of this selector is unaffected. */
  openPencilImportActivity?: OpenPencilImportActivity | null;
}

function cockpitDomainForRailItem(railItem: RailItemId): StudioAuthorityDomain {
  // resolveStudioAuthorityDomain never actually returns "as-is" (no
  // RailItemId maps to it — the As-Is authority task parks activeRailItem
  // on "source", which this already resolves to "design"), but its
  // declared return type is the full StudioAuthorityDomain (same
  // narrower-body/wider-type pattern as resolveStudioArtifactMode in
  // artifact-mode-switcher.tsx), so this function's own return type widened
  // to match rather than narrow it back with a cast.
  return resolveStudioAuthorityDomain(railItem) ?? "primary";
}

function cockpitTabForDomain(domain: StudioAuthorityDomain): StudioCockpitTab {
  if (domain === "primary") return "app";
  if (domain === "insights") return "status";
  // Cockpit-attention tracking predates the As-Is mode (G1 alternative A)
  // and has no lane for it (same reasoning as toCockpitTab in
  // artifact-mode-switcher.tsx). Unreached via cockpitDomainForRailItem
  // (see above), but `destination.domain` below can carry "as-is" directly
  // (StudioCockpitDestination.domain: StudioAuthorityDomain), so this stays
  // exhaustive rather than an implicit fallthrough.
  if (domain === "as-is") return "app";
  return domain;
}

function isoTime(value: number | null | undefined): string {
  return new Date(value ?? 0).toISOString();
}

function withReviewedState(
  record: StudioCockpitActivity,
  reviewedIds: readonly string[],
): StudioCockpitActivity {
  return reviewedIds.includes(record.id)
    ? { ...record, reviewedAt: record.createdAt }
    : record;
}

function dispatchKind(
  phase: ChannelDispatchStatus["phase"],
): StudioCockpitActivityKind | null {
  if (
    phase === "request-in-flight" ||
    phase === "pattern-retrieval" ||
    phase === "mock-generation" ||
    phase === "real-generation" ||
    phase === "rendering"
  ) {
    return "working";
  }
  if (phase === "completed") return "changed";
  if (phase === "failed" || phase === "aborted") return "blocked";
  return null;
}

export function selectStudioCockpitActivities(
  state: StudioCockpitSelectorState,
): StudioCockpitActivity[] {
  const activities: StudioCockpitActivity[] = [];
  const dispatchStatus = state.channelDispatchStatus;
  const kind = dispatchKind(dispatchStatus.phase);

  if (kind) {
    const timestamp = dispatchStatus.updatedAt ?? dispatchStatus.startedAt ?? 0;
    activities.push({
      id: `chat-dispatch:${timestamp}`,
      tab: "app",
      kind,
      source: "chat",
      title: dispatchStatus.message ?? "Coogo application work",
      operationId: `chat-dispatch:${dispatchStatus.startedAt ?? timestamp}`,
      createdAt: isoTime(timestamp),
      destination: APP_OVERVIEW,
      resolution: "presented",
    });
  }

  const transaction = state.agenticUxTransaction;
  if (transaction) {
    const destination = transaction.destination ?? {
      domain: cockpitDomainForRailItem(state.activeRailItem),
      authorityView: "overview",
      railItem: state.activeRailItem,
      targetRef: transaction.reference ?? undefined,
    };
    const transactionKind: StudioCockpitActivityKind =
      transaction.status === "running"
        ? "working"
        : transaction.status === "failed"
          ? "blocked"
          : "changed";
    activities.push({
      id: `webmcp:${transaction.id}`,
      tab: cockpitTabForDomain(destination.domain),
      kind: transactionKind,
      source: "webmcp",
      title: transaction.error ?? transaction.reference ?? "Coogo operation",
      operationId: transaction.id,
      targetRef: transaction.reference ?? undefined,
      createdAt: isoTime(transaction.updatedAt),
      destination,
      resolution:
        transaction.status === "failed"
          ? "explicit-decision"
          : "presented",
    });
  }

  if (state.validatorRunning) {
    activities.push({
      id: `validation:${state.validatorLastRunAt ?? "running"}`,
      tab: "status",
      kind: "working",
      source: "validation",
      title: "Checking product readiness",
      operationId: `validation:${state.validatorLastRunAt ?? "running"}`,
      createdAt: isoTime(state.validatorLastRunAt),
      userActionRequired: false,
      destination: STATUS_QUALITY,
      resolution: "authoritative-success",
    });
  }
  const unresolvedValidationError =
    state.validatorUnresolvedError ??
    (state.validatorError
      ? {
          message: state.validatorError,
          failedAt: state.validatorLastRunAt ?? 0,
        }
      : null);
  if (unresolvedValidationError) {
    activities.push({
      id: `validation:${unresolvedValidationError.failedAt}`,
      tab: "status",
      kind: "blocked",
      source: "validation",
      title: unresolvedValidationError.message,
      operationId: `validation:${unresolvedValidationError.failedAt}`,
      createdAt: isoTime(unresolvedValidationError.failedAt),
      userActionRequired: true,
      destination: STATUS_QUALITY,
      resolution: "authoritative-success",
    });
  }

  // Source (.fig) import journey. `importing` is progress, so it must NOT
  // demand attention (userActionRequired: false — same treatment as a running
  // validator); `blocked` is a real blocker and does.
  const sourceImport = state.openPencilImportActivity;
  if (sourceImport) {
    const blocked = sourceImport.phase === "blocked";
    const timestamp = blocked ? sourceImport.updatedAt : sourceImport.startedAt;
    activities.push({
      id: `source-import:${sourceImport.phase}:${timestamp}`,
      tab: "status",
      kind: blocked ? "blocked" : "working",
      source: "manual",
      title: openPencilImportActivityTitle(sourceImport),
      operationId: `source-import:${sourceImport.startedAt}`,
      targetRef: sourceImport.fileName,
      createdAt: isoTime(timestamp),
      userActionRequired: blocked,
      destination: DESIGN_SOURCES,
      resolution: blocked ? "explicit-decision" : "presented",
    });
  }

  return activities.map((record) =>
    withReviewedState(record, state.reviewedCockpitActivityIds),
  );
}
